import { Injectable } from '@angular/core';
import { DICTIONARY_WORDS } from 'src/assets/dictionary/dictionary.words';

@Injectable({ providedIn: 'root' })
export class DictionaryService {
    private trie = new Trie();

    constructor() {
        this.buildTrie();
    }

    private buildTrie(): void {
        // Insert all dictionary words into the trie (lowercase)
        for (const word of DICTIONARY_WORDS) {
            this.trie.insert(word.toLowerCase());
        }
    }

    /**
     * Checks if the password contains any dictionary word of at least `minLength`.
     * @param password The password to check.
     * @param minLength Minimum length of words to check (default: 4).
     * @returns `true` if a dictionary word is found, `false` otherwise.
     */
    containsDictionaryWord(password: string, minLength: number = 4): boolean {
        if (!password || password.length < minLength) {
            return false;
        }
        return this.trie.containsAnySubstring(password.toLowerCase(), minLength);
    }
}

/**
 * Trie Node: Represents a single character in the Trie.
 */
class TrieNode {
    children: Map<string, TrieNode> = new Map();
    isEndOfWord: boolean = false;
}

/**
 * Trie Data Structure for efficient dictionary word lookups.
 */
class Trie {
    private root = new TrieNode();

    /**
     * Inserts a word into the trie.
     * @param word The word to insert (should be lowercase).
     */
    insert(word: string): void {
        let node = this.root;
        for (const char of word) {
            if (!node.children.has(char)) {
                node.children.set(char, new TrieNode());
            }
            node = node.children.get(char);
        }
        node.isEndOfWord = true; // Mark the end of a valid word
    }

    /**
     * Checks if the text contains any substring that exists in the trie
     * and meets the minimum length requirement.
     * @param text The text to check (should be lowercase).
     * @param minLength Minimum length of words to consider.
     * @returns `true` if a matching substring is found, `false` otherwise.
     */
    containsAnySubstring(text: string, minLength: number): boolean {
        for (let i = 0; i < text.length; i++) {
            let node = this.root;
            let currentLength = 0;

            for (let j = i; j < text.length; j++) {
                const char = text[j];
                if (!node.children.has(char)) {
                    break; // No further matches possible from this position
                }

                node = node.children.get(char);
                currentLength++;

                if (node.isEndOfWord && currentLength >= minLength) {
                    return true; // Found a dictionary word
                }
            }
        }
        return false;
    }
}